import { useState, useRef, useEffect } from "react";
import {
  ArrowLeft,
  Check,
  Plus,
  Trash2,
  MoveUp,
  MoveDown,
  ChevronDown,
  ChevronUp,
  FileText,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useCreatePost } from "@/hooks/blog/useCreatePost";
import { useCategories } from "@/hooks/blog/useCategories";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import RichTextEditor from "@/components/ui/rich-text-editor";
import LivePreview from "@/components/ui/live-preview";
import { ImageUploader } from "@/components/ui/image-uploader";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";

export default function BlogCreate() {
  const [formData, setFormData] = useState({
    title: "",
    excerpt: "",
    category_id: "",
    featured_image: "",
    is_published: false,
    is_featured: false,
    tags: "",
  });

  // Store pending file for featured image (not yet uploaded)
  const [pendingFeaturedImage, setPendingFeaturedImage] = useState(null);

  const [sections, setSections] = useState([
    { id: Date.now(), title: "", content: "", image: null, imagePreview: null, isOpen: true },
  ]);

  const [imagePreview, setImagePreview] = useState(null);
  const [errors, setErrors] = useState({});

  // TanStack Query hooks
  const { data: categoriesData } = useCategories();
  const createPostMutation = useCreatePost();

  const categories = categoriesData?.data || [];
  const isSubmitting = createPostMutation.isPending;

  // Component to conditionally show tooltip only when text is truncated
  const TextWithTooltip = ({ text, lineClamp = "truncate", className = "" }) => {
    const textRef = useRef(null);
    const [isTruncated, setIsTruncated] = useState(false);

    useEffect(() => {
      const checkTruncation = () => {
        if (textRef.current) {
          // Check if text is truncated by comparing scrollWidth/scrollHeight with clientWidth/clientHeight
          const isClamped =
            textRef.current.scrollHeight > textRef.current.clientHeight ||
            textRef.current.scrollWidth > textRef.current.clientWidth;
          setIsTruncated(isClamped);
        }
      };

      checkTruncation();
      // Recheck on window resize
      window.addEventListener("resize", checkTruncation);
      return () => window.removeEventListener("resize", checkTruncation);
    }, [text]);

    const textElement = (
      <div
        ref={textRef}
        className={`${lineClamp} ${className}`}
        style={{ cursor: isTruncated ? "help" : "default" }}>
        {text}
      </div>
    );

    if (!isTruncated) {
      return textElement;
    }

    return (
      <Tooltip>
        <TooltipTrigger asChild>{textElement}</TooltipTrigger>
        <TooltipContent side="top" className="max-w-md">
          <p>{text}</p>
        </TooltipContent>
      </Tooltip>
    );
  };

  // Component to conditionally show tooltip only when content is truncated
  const ContentWithTooltip = ({ content }) => {
    const contentRef = useRef(null);
    const [isTruncated, setIsTruncated] = useState(false);

    useEffect(() => {
      const checkTruncation = () => {
        if (contentRef.current) {
          // Check if content is truncated by comparing scrollHeight with clientHeight
          const isClamped = contentRef.current.scrollHeight > contentRef.current.clientHeight;
          setIsTruncated(isClamped);
        }
      };

      checkTruncation();
      // Recheck on window resize
      window.addEventListener("resize", checkTruncation);
      return () => window.removeEventListener("resize", checkTruncation);
    }, [content]);

    const contentElement = (
      <div
        ref={contentRef}
        className="line-clamp-4 overflow-hidden"
        style={{ cursor: isTruncated ? "help" : "default" }}>
        <div
          className="prose prose-sm max-w-none text-gray-700 *:my-0 *:leading-relaxed"
          dangerouslySetInnerHTML={{ __html: content }}
        />
      </div>
    );

    if (!isTruncated) {
      return contentElement;
    }

    return (
      <Tooltip>
        <TooltipTrigger asChild>{contentElement}</TooltipTrigger>
        <TooltipContent side="top" className="max-h-96 max-w-md overflow-y-auto">
          <div
            className="prose prose-sm max-w-none text-xs"
            dangerouslySetInnerHTML={{ __html: content }}
          />
        </TooltipContent>
      </Tooltip>
    );
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const toggleBooleanField = (field) => {
    setFormData((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  // Store file locally, defer upload until submit
  const handleImageSelect = (file, previewUrl) => {
    setPendingFeaturedImage(file);
    setImagePreview(previewUrl);
    setFormData((prev) => ({ ...prev, featured_image: "" })); // Clear any existing URL
    setErrors((prev) => ({ ...prev, featured_image: "" }));
  };

  const handleRemoveImage = () => {
    setPendingFeaturedImage(null);
    setFormData((prev) => ({ ...prev, featured_image: "" }));
    setImagePreview(null);
  };

  const addSection = () => {
    setSections((prev) => [
      ...prev.map((s) => ({ ...s, isOpen: false })),
      { id: Date.now(), title: "", content: "", image: null, imagePreview: null, isOpen: true },
    ]);
  };

  const toggleSection = (id) => {
    setSections(sections.map((s) => (s.id === id ? { ...s, isOpen: !s.isOpen } : s)));
  };

  const removeSection = (id) => {
    if (sections.length === 1) {
      toast.error("You must have at least one section");
      return;
    }
    setSections(sections.filter((section) => section.id !== id));
  };

  const updateSection = (id, field, value) => {
    setSections(
      sections.map((section) => (section.id === id ? { ...section, [field]: value } : section))
    );
  };

  const moveSection = (index, direction) => {
    if (
      (direction === "up" && index === 0) ||
      (direction === "down" && index === sections.length - 1)
    )
      return;
    const newSections = [...sections];
    const swapIndex = direction === "up" ? index - 1 : index + 1;
    [newSections[swapIndex], newSections[index]] = [newSections[index], newSections[swapIndex]];
    setSections(newSections);
  };

  const handleSectionImageSelect = (sectionId, file, previewUrl) => {
    setSections(
      sections.map((section) =>
        section.id === sectionId ? { ...section, image: file, imagePreview: previewUrl } : section
      )
    );
  };

  const handleRemoveSectionImage = (sectionId) => {
    setSections(
      sections.map((section) =>
        section.id === sectionId ? { ...section, image: null, imagePreview: null } : section
      )
    );
  };

  const escapeHtml = (text) => {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  };

  // Generate content with placeholders for section images
  const generateContentFromSections = () => {
    let imageIndex = 0;
    return sections
      .map((section) => {
        let html = "";
        if (section.title.trim()) html += `<h2>${escapeHtml(section.title)}</h2>\n`;
        if (section.image) {
          // Use placeholder that backend will replace with actual URL
          html += `<img src="__SECTION_IMAGE_${imageIndex}__" alt="${escapeHtml(section.title || "Section image")}" />\n`;
          imageIndex++;
        }
        if (section.content.trim()) {
          html += section.content + "\n";
        }
        return html;
      })
      .join("\n");
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = "Title is required";
    if (!formData.excerpt.trim()) newErrors.excerpt = "Excerpt is required";
    if (!formData.category_id) newErrors.category_id = "Category is required";
    const hasContent = sections.some((section) => section.title.trim() || section.content.trim());
    if (!hasContent) newErrors.sections = "At least one section must have content";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const generatedContent = generateContentFromSections();
    const tags = formData.tags
      .split(",")
      .map((tag) => tag.trim())
      .filter(Boolean);

    // Collect section images in order
    const sectionImages = sections
      .filter((section) => section.image instanceof File)
      .map((section) => section.image);

    const postData = {
      title: formData.title,
      excerpt: formData.excerpt,
      content: generatedContent,
      category_id: parseInt(formData.category_id),
      featured_image: pendingFeaturedImage || null,
      is_published: formData.is_published,
      is_featured: formData.is_featured,
      tags: tags.length > 0 ? tags : undefined,
      section_images: sectionImages.length > 0 ? sectionImages : undefined,
    };

    createPostMutation.mutate(postData);
  };

  return (
    <div className="mx-auto w-full max-w-450 space-y-8 p-6 lg:p-10">
      <div className="flex items-center gap-4">
        <Link to="/dashboard/blogs">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="outline" size="icon" className="h-10 w-10">
                <ArrowLeft className="h-5 w-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">Back to posts</TooltipContent>
          </Tooltip>
        </Link>
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-gray-900">Create Post</h1>
          <p className="text-muted-foreground mt-1 text-lg">Draft a new article for your blog.</p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {/* UPDATED: Increased gap to 12 and Right Column (Sidebar) to 500px */}
        <div className="grid items-start gap-12 lg:grid-cols-[1fr_500px]">
          {/* LEFT COLUMN - FORM */}
          <div className="space-y-8">
            {/* 1. Basic Info Card */}
            <Card className="shadow-sm">
              <CardHeader>
                <CardTitle className="text-xl">Post Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="title" className="text-base">
                    Title <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="Enter an engaging title"
                    className={`h-12 text-lg font-medium ${errors.title ? "border-red-500" : ""}`}
                  />
                  {errors.title && <p className="text-sm text-red-500">{errors.title}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="excerpt" className="text-base">
                    Excerpt <span className="text-red-500">*</span>
                  </Label>
                  <textarea
                    id="excerpt"
                    name="excerpt"
                    value={formData.excerpt}
                    onChange={handleInputChange}
                    placeholder="Brief summary appearing on the blog card..."
                    rows={4}
                    className={`flex w-full rounded-md border ${
                      errors.excerpt ? "border-red-500" : "border-input"
                    } bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring px-4 py-3 text-base focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50`}
                  />
                  {errors.excerpt && <p className="text-sm text-red-500">{errors.excerpt}</p>}
                </div>
              </CardContent>
            </Card>

            {/* 2. Sections Area */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="flex items-center gap-2 text-xl font-semibold">
                  <FileText className="h-6 w-6" /> Content Sections
                </h3>
                <Button className="cursor-pointer" type="button" onClick={addSection} size="lg">
                  <Plus className="mr-2 h-5 w-5" /> Add Section
                </Button>
              </div>

              {errors.sections && (
                <p className="rounded bg-red-50 p-2 text-sm text-red-500">{errors.sections}</p>
              )}

              <div className="space-y-6">
                {sections.map((section, index) => (
                  <Card
                    key={section.id}
                    className={`shadow-sm transition-all duration-200 ${section.isOpen ? "ring-primary/20 ring-1" : "opacity-80 hover:opacity-100"}`}>
                    <div className="bg-muted/20 flex items-center justify-between border-b p-5">
                      <div
                        className="flex cursor-pointer items-center gap-3"
                        onClick={() => toggleSection(section.id)}>
                        {section.isOpen ? (
                          <ChevronDown className="text-muted-foreground h-5 w-5" />
                        ) : (
                          <ChevronUp className="text-muted-foreground h-5 w-5" />
                        )}
                        <span className="text-base font-semibold">Section {index + 1}</span>
                        {section.title && (
                          <span className="text-muted-foreground ml-2 max-w-75 truncate border-l pl-3 text-sm">
                            {section.title}
                          </span>
                        )}
                      </div>
                      <div className="flex gap-1">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              type="button"
                              onClick={() => moveSection(index, "up")}
                              size="icon"
                              variant="ghost"
                              className="h-9 w-9"
                              disabled={index === 0}>
                              <MoveUp className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="top">Move section up</TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              type="button"
                              onClick={() => moveSection(index, "down")}
                              size="icon"
                              variant="ghost"
                              className="h-9 w-9"
                              disabled={index === sections.length - 1}>
                              <MoveDown className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="top">Move section down</TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              type="button"
                              onClick={() => removeSection(section.id)}
                              size="icon"
                              variant="ghost"
                              className="h-9 w-9 text-red-500 hover:bg-red-50 hover:text-red-600 cursor-pointer">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="top">Delete section</TooltipContent>
                        </Tooltip>
                      </div>
                    </div>

                    {section.isOpen && (
                      <CardContent className="space-y-6 p-6 pt-8">
                        <div className="space-y-3">
                          <Label className="text-base">Section Title</Label>
                          <Input
                            value={section.title}
                            onChange={(e) => updateSection(section.id, "title", e.target.value)}
                            placeholder="Section heading..."
                            className="h-11 text-base"
                          />
                        </div>
                        <div className="space-y-3">
                          <Label className="text-base">Section Image</Label>
                          <ImageUploader
                            id={`section-image-${section.id}`}
                            preview={section.imagePreview}
                            onImageSelect={(file, previewUrl) =>
                              handleSectionImageSelect(section.id, file, previewUrl)
                            }
                            onImageRemove={() => handleRemoveSectionImage(section.id)}
                            disabled={isSubmitting}
                          />
                        </div>
                        <div className="space-y-3">
                          <Label className="text-base">Content</Label>
                          <RichTextEditor
                            value={section.content}
                            onChange={(content) => updateSection(section.id, "content", content)}
                            placeholder="Write your content here..."
                            height={350}
                          />
                          <p className="text-muted-foreground text-right text-sm">
                            Use the toolbar to format your content.
                          </p>
                        </div>
                      </CardContent>
                    )}
                  </Card>
                ))}
              </div>
            </div>

            {/* Bottom Organization & Metadata */}
            <div className="grid gap-8 md:grid-cols-2">
              <Card className="shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg">Organization</CardTitle>
                </CardHeader>
                <CardContent className="space-y-5">
                  <div className="space-y-3">
                    <Label htmlFor="category_id" className="text-base">
                      Category <span className="text-red-500">*</span>
                    </Label>
                    <Select
                      key={`category-select-${formData.category_id}`}
                      value={formData.category_id}
                      onValueChange={(val) => {
                        setFormData((p) => ({ ...p, category_id: val }));
                        setErrors((p) => ({ ...p, category_id: "" }));
                      }}>
                      <SelectTrigger
                        className={`h-11 ${errors.category_id ? "border-red-500" : ""}`}>
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((cat) => (
                          <SelectItem key={cat.id} value={cat.id.toString()}>
                            {cat.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.category_id && (
                      <p className="text-sm text-red-500">{errors.category_id}</p>
                    )}
                  </div>

                  <div className="space-y-3">
                    <Label htmlFor="tags" className="text-base">
                      Tags
                    </Label>
                    <Input
                      id="tags"
                      name="tags"
                      value={formData.tags}
                      onChange={handleInputChange}
                      placeholder="health, news, update"
                      className="h-11"
                    />
                    <p className="text-muted-foreground text-sm">Comma separated</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg">Featured Image</CardTitle>
                </CardHeader>
                <CardContent>
                  <ImageUploader
                    id="featured_image"
                    preview={imagePreview}
                    onImageSelect={handleImageSelect}
                    onImageRemove={handleRemoveImage}
                    disabled={isSubmitting}
                  />
                  {errors.featured_image && (
                    <p className="mt-2 text-sm text-red-500">{errors.featured_image}</p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Publishing */}
            <Card className="shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg">Publishing</CardTitle>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="grid gap-6 md:grid-cols-2">
                  <button
                    type="button"
                    onClick={() => toggleBooleanField("is_published")}
                    className="flex cursor-pointer items-center justify-between rounded-lg border p-4 text-left transition-colors hover:bg-gray-50"
                    aria-pressed={formData.is_published}>
                    <Label className="cursor-pointer text-base">Publish now</Label>
                    <span
                      className={`flex h-5 w-5 items-center justify-center rounded border transition-colors ${
                        formData.is_published
                          ? "border-[#2546A1] bg-[#2546A1] text-white"
                          : "border-gray-300 bg-white text-transparent"
                      }`}>
                      <Check className="h-3.5 w-3.5" />
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => toggleBooleanField("is_featured")}
                    className="flex cursor-pointer items-center justify-between rounded-lg border p-4 text-left transition-colors hover:bg-gray-50"
                    aria-pressed={formData.is_featured}>
                    <Label className="cursor-pointer text-base">Mark as Featured</Label>
                    <span
                      className={`flex h-5 w-5 items-center justify-center rounded border transition-colors ${
                        formData.is_featured
                          ? "border-[#2546A1] bg-[#2546A1] text-white"
                          : "border-gray-300 bg-white text-transparent"
                      }`}>
                      <Check className="h-3.5 w-3.5" />
                    </span>
                  </button>
                </div>

                {errors.submit && (
                  <div className="rounded-md border border-red-200 bg-red-50 p-4">
                    <p className="text-sm text-red-600">{errors.submit}</p>
                  </div>
                )}

                <div className="flex gap-3 border-t pt-6">
                  <Link to="/dashboard/blogs" className="flex-1">
                    <Button type="button" variant="outline" size="lg" className="w-full cursor-pointer">
                      Cancel
                    </Button>
                  </Link>
                  <Button type="submit" size="lg" className="flex-1 cursor-pointer" disabled={isSubmitting}>
                    {isSubmitting ? <Spinner className="mr-2 h-4 w-4" /> : "Save Post"}
                  </Button>
                </div>
              </CardContent>  
            </Card>
          </div>

          {/* RIGHT COLUMN - LIVE PREVIEW */}
          <LivePreview title="Live Preview" description="Preview how your blog post will look">
            {/* Preview Content */}
            <div className="space-y-4">
              {imagePreview && (
                <div className="aspect-video overflow-hidden rounded-lg">
                  <img src={imagePreview} alt="Featured" className="h-full w-full object-cover" />
                </div>
              )}

              {formData.title && (
                <TextWithTooltip
                  text={formData.title}
                  lineClamp="truncate"
                  className="text-2xl font-bold text-gray-900"
                />
              )}

              <div className="flex flex-wrap gap-2">
                {formData.category_id && (
                  <span className="rounded bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800">
                    {categories.find((c) => c.id.toString() === formData.category_id)?.name ||
                      "Category"}
                  </span>
                )}
                {formData.tags
                  .split(",")
                  .filter(Boolean)
                  .map((tag, i) => (
                    <span
                      key={i}
                      className="rounded bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-800">
                      {tag.trim()}
                    </span>
                  ))}
              </div>

              {formData.excerpt && (
                <TextWithTooltip
                  text={formData.excerpt}
                  lineClamp="line-clamp-3"
                  className="border-l-4 border-gray-300 pl-4 text-sm leading-relaxed text-gray-600 italic"
                />
              )}

              {sections.map((section, index) => (
                <div key={section.id} className="space-y-3 border-t pt-4">
                  {section.title && (
                    <TextWithTooltip
                      text={section.title}
                      lineClamp="truncate"
                      className="text-xl font-semibold text-gray-900"
                    />
                  )}
                  {section.imagePreview && (
                    <div className="overflow-hidden rounded-lg">
                      <img
                        src={section.imagePreview}
                        alt={section.title || `Section ${index + 1}`}
                        className="w-full object-cover"
                      />
                    </div>
                  )}
                  {section.content && <ContentWithTooltip content={section.content} />}
                </div>
              ))}

              {!formData.title &&
                !formData.excerpt &&
                sections.every((s) => !s.title && !s.content) && (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <FileText className="mb-3 h-12 w-12 text-gray-300" />
                    <p className="text-sm text-gray-500">
                      Start filling in the form to see preview
                    </p>
                  </div>
                )}
            </div>
          </LivePreview>
        </div>
      </form>
    </div>
  );
}
